import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutInfo, siteConfig } from '@/mocks/siteData';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.domain,
    description: aboutInfo.brandIntro,
    foundingDate: siteConfig.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutInfo.contact.phone,
      contactType: 'customer service',
      email: aboutInfo.contact.email,
      availableLanguage: ['Chinese'],
    },
    sameAs: [
      'https://www.kaiyunsports.com',
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: aboutInfo.brandIntro,
    url: 'https://www.kaiyunsports.com/about',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                  关于开云体育
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                  {siteConfig.slogan}
                </p>
              </div>

              <div className="relative h-64 md:h-80 rounded-xl overflow-hidden mb-12">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20luxury%20sports%20headquarters%20building%20interior%20with%20warm%20lighting%2C%20green%20and%20gold%20decor%2C%20professional%20corporate%20atmosphere%2C%20elegant%20design%2C%20clean%20minimal%20aesthetic%2C%20high%20end%20office%20photography&width=1200&height=600&seq=kaiyun-about-hero&orientation=landscape"
                  alt="开云体育品牌形象"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mb-16">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                  品牌介绍
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  {aboutInfo.brandIntro}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
                  <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                      <i className="ri-focus-3-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 mb-2 text-base">企业使命</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{aboutInfo.mission}</p>
                  </div>
                  <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-eye-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 mb-2 text-base">企业愿景</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{aboutInfo.vision}</p>
                  </div>
                </div>
              </div>

              <div className="mb-16">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                  核心价值观
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {aboutInfo.values.map((value, index) => (
                    <div
                      key={index}
                      className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center"
                    >
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500 text-background-50 mx-auto mb-3">
                        <i className={`${['ri-shield-star-line', 'ri-lightbulb-line', 'ri-heart-line', 'ri-flag-line'][index]} text-lg w-6 h-6 flex items-center justify-center`}></i>
                      </div>
                      <h3 className="font-semibold text-foreground-900 mb-1.5 text-sm">{value.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-16">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                  发展历程
                </h2>
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 md:-translate-x-px"></div>
                  <div className="space-y-6">
                    {aboutInfo.milestones.map((milestone, index) => (
                      <div
                        key={index}
                        className={`relative flex flex-col md:flex-row gap-4 ${
                          index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                        }`}
                      >
                        <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-10' : 'md:text-left md:pl-10'}`}>
                          <div className="hidden md:block p-5 rounded-xl bg-background-100 border border-background-200/70">
                            <span className="font-heading text-lg font-bold text-primary-600">{milestone.year}</span>
                            <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                          </div>
                        </div>
                        <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                        <div className="flex-1 md:hidden pl-10">
                          <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                            <span className="font-heading text-base font-bold text-primary-600">{milestone.year}</span>
                            <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                          </div>
                        </div>
                        <div className={`flex-1 hidden md:block ${index % 2 === 0 ? 'md:pl-10' : 'md:pr-10'}`}>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div id="contact">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-3">
                      <i className="ri-mail-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 mb-1 text-sm">电子邮箱</h3>
                    <p className="text-xs text-foreground-500">{aboutInfo.contact.email}</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-3">
                      <i className="ri-phone-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 mb-1 text-sm">客服热线</h3>
                    <p className="text-xs text-foreground-500">{aboutInfo.contact.phone}</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-3">
                      <i className="ri-wechat-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 mb-1 text-sm">官方微信</h3>
                    <p className="text-xs text-foreground-500">{aboutInfo.contact.wechat}</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-3">
                      <i className="ri-time-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 mb-1 text-sm">服务时间</h3>
                    <p className="text-xs text-foreground-500">{aboutInfo.contact.hours}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}